import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import HeirloomView from '@/components/HeirloomView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeirloomPreview() {
  const { projectId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) fetchPreview();
  }, [user, projectId]);

  const fetchPreview = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.get(`${API}/heirloom/preview/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setProject(data);
    } catch {
      setError('Could not load preview.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A89F91] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center px-6">
        <p className="text-[#57534E]">Please log in to access preview.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center px-6">
        <p className="text-[#57534E]">{error}</p>
      </div>
    );
  }

  return (
    <div data-testid="heirloom-preview-page">
      {/* Preview Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between" data-testid="preview-banner">
        <div className="flex items-center gap-2 text-amber-800 text-sm">
          <AlertTriangle size={16} />
          <span className="font-medium">Admin Preview</span>
          <span className="hidden sm:inline text-amber-600">— This page is not yet published</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/admin/projects/${projectId}`} className="text-xs text-amber-800 border border-amber-300 hover:bg-amber-100 px-3 py-1 transition-colors" data-testid="preview-edit-link">
            <ArrowLeft size={12} className="inline mr-1" /> Edit Project
          </Link>
        </div>
      </div>
      <div className="pt-10">
        {project && <HeirloomView project={project} />}
      </div>
    </div>
  );
}
